import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, ViewDidEnter, ViewWillEnter } from '@ionic/angular';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { OrderItemService } from '@shared/services/order-item.service';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Observable } from 'rxjs';
import { Product } from '../shared/models/product.model';
import { BetterBuysPageViewModelV2 } from './models/better-buys.model';
import { BetterBuysService } from './services/better-buys.service';
import { BetterBuyDeclineEvent } from '@shared/models/better-buy-card.model';
import { PageTitle } from '../shared/constants/page-titles.enum';
import { BetterBuysConstants } from './constants/better-buys-constants.enum';
import { TranslateService } from '@ngx-translate/core';
import { ProductConversionContainer } from '@shared/models/product-conversion.model';
import { PATHS } from '@shared/constants/paths';
import { ProductCompareEnum } from '@shared/constants/product-compare.enum';
import { ProductClickData } from '@shared/models/usf-product-card.model';
import { ProductAnalyticsService } from '@usf/ngrx-product';

@Component({
  selector: 'app-better-buys',
  templateUrl: './better-buys.page.html',
  styleUrls: ['./better-buys.page.scss'],
})
export class BetterBuysPage implements ViewWillEnter, ViewDidEnter {
  @ViewChild(IonContent) content: IonContent;
  betterBuysViewModelV2$: Observable<BetterBuysPageViewModelV2>;
  platform: PlatformEnum;
  totalAnnualSavings$: Observable<number>;
  estimatedAllTimeSavings$: Observable<number>;
  estimatedTodaySavings$: Observable<number>;
  estimatedYearToDateSavings$: Observable<number>;

  constructor(
    private betterBuysService: BetterBuysService,
    private route: ActivatedRoute,
    private orderItemService: OrderItemService,
    readonly platformService: PlatformService,
    private panAppState: PanAppState,
    private navigationHelperService: NavigationHelperService,
    private translateService: TranslateService,
    private analyticsService: ProductAnalyticsService,
  ) {}

  ionViewWillEnter(): void {
    this.betterBuysViewModelV2$ =
      this.betterBuysService.selectBetterBuysViewModel$(
        this.route.snapshot.data.productNumbers,
      );
    this.platform = this.platformService.platformType;
    this.totalAnnualSavings$ = this.betterBuysService.totalAnnualSavings$();
    this.estimatedAllTimeSavings$ =
      this.betterBuysService.estimatedAllTimeSavings$();
    this.estimatedTodaySavings$ =
      this.betterBuysService.estimatedTodaySavings$();
    this.estimatedYearToDateSavings$ =
      this.betterBuysService.estimatedYearToDateSavings$();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.betterBuys),
    );
    this.betterBuysService.trackBetterBuysPageLoad();
  }

  ionViewDidEnter(): void {
    this.content?.scrollToTop();
  }

  changeQuantityHandler(event: any) {
    const { quantityUpdate, productConversionContainer } = event;

    if (productConversionContainer) {
      const tracking = this.betterBuysService.createProductsForTracking(
        productConversionContainer,
      );
      this.orderItemService.changeQuantity(
        quantityUpdate.productNumber,
        quantityUpdate.quantity,
        quantityUpdate.isEaches,
        tracking,
      );
    } else {
      this.orderItemService.changeQuantity(
        quantityUpdate.productNumber,
        quantityUpdate.quantity,
        quantityUpdate.isEaches,
      );
    }
  }

  acceptSwap(betterBuysWithDetails: ProductConversionContainer) {
    const product = {
      ...betterBuysWithDetails.originalProduct,
      betterBuy: {
        product: betterBuysWithDetails.replacementProduct,
        savings: betterBuysWithDetails.replacementProduct.savings,
        savingsUom: betterBuysWithDetails.replacementProduct.savingsUom,
        annualSavingsUom:
          betterBuysWithDetails.replacementProduct.annualSavingsUom,
        annualSavings: betterBuysWithDetails.replacementProduct.annualSavings,
        reason: betterBuysWithDetails.replacementProduct.reasonCode,
      },
    } as Product;
    this.betterBuysService.openAcceptSwapModal(
      product,
      false,
      BetterBuysConstants.listsCapability,
    );
  }

  declineSwap(betterBuyDeclineEvent: ProductConversionContainer) {
    const declineSwap = {
      original: betterBuyDeclineEvent.originalProduct,
      betterBuy: {
        product: betterBuyDeclineEvent.replacementProduct,
        savings: betterBuyDeclineEvent.replacementProduct.savings,
        savingsUom: betterBuyDeclineEvent.replacementProduct.savingsUom,
        annualSavingsUom:
          betterBuyDeclineEvent.replacementProduct.annualSavingsUom,
        annualSavings: betterBuyDeclineEvent.replacementProduct.annualSavings,
        reason: betterBuyDeclineEvent.replacementProduct.reasonCode,
      },
    } as BetterBuyDeclineEvent;
    this.betterBuysService.openDeclineModal(declineSwap);
  }

  goToCompareProducts(container: ProductConversionContainer) {
    this.navigationHelperService.routeTo(PATHS.SEARCH_COMPARE, {
      selectedProducts: `${container.originalProduct.productNumber},${container.replacementProduct.productNumber}`,
      originalItem: container.originalProduct.productNumber,
      mode: ProductCompareEnum.betterBuyCompare,
    });
  }

  trackProductImageClick(productClickData: ProductClickData) {
    this.analyticsService.trackProductImageClick(
      productClickData?.product?.summary?.divisionNumber,
      productClickData?.productNumber,
      productClickData?.product?.trackingAttributes,
      productClickData?.product?.resultRank,
    );
  }

  handleProductImageClick(productClickData: ProductClickData) {
    this.trackProductImageClick(productClickData);
    this.navigationHelperService.routeToProductDetailsQueryParams(
      productClickData.productNumber,
      {
        lnksrc: productClickData.currentProduct
          ? 'betterBuysOriginal'
          : 'betterBuysSuggestion',
      },
    );
  }

  handleDownload = (
    productConversionContainers: ProductConversionContainer[],
  ) => {
    this.betterBuysService.downloadBetterBuys(productConversionContainers);
  };

  handlePrint = (productConversionContainers: ProductConversionContainer[]) => {
    this.betterBuysService.printBetterBuys(productConversionContainers);
  };

  handleMobileEllipsis = (
    productConversionContainers: ProductConversionContainer[],
  ) => {
    this.betterBuysService.openEllipsis(productConversionContainers);
  };
}
